import { Link } from '@remix-run/react'
import React from 'react'
import {
  EXTERNAL_LINK_PROPS,
  getEncargaUnCocheUrl,
} from '#app/shared/lib/external-links.ts'
import { Separator } from '../data-display/Separator'
import { Icon } from '../icons/Icon'
import { Button } from '../inputs/button/Button'
import HeaderTopBanner from './HeaderTopBanner'

export interface DrawerItem {
  title: string
  icon: React.ReactNode // Change the type to React.ReactNode
  href: string
  isExactPath?: boolean
}

// "Encarga un coche" apunta a la app externa (gamaoptima.net), no a la ruta
// interna /encarga-un-coche, que ya no se usa. Se construye en cada render
// porque la URL depende de `ENV`, que no existe al importar el módulo.
const getDrawerLinks = () => [
  {
    title: 'Nosotros',
    href: '/sobre-nosotros',
  },
  {
    title: 'Nuestros coches',
    href: '/nuestros-coches',
  },
  {
    title: 'Encarga un coche',
    href: getEncargaUnCocheUrl(),
  },
  {
    title: 'Vende tu coche',
    href: '/vende-tu-coche',
  },
  // {
  //   title: 'Financiación',
  //   href: '/financia',
  // },
]

interface LeftDrawerNavigatorProps {
  //   items: DrawerItem[]
  isOpen: boolean | undefined
  onClose: () => void
  isActive: (item: { path: string }) => boolean
}

const DrawerNavigator = ({
  //   items,
  isOpen,
  onClose,
  isActive,
}: LeftDrawerNavigatorProps) => {
  const isOpenStyles = () => {
    return isOpen ? '!w-full' : '!w-[0px] invisible'
  }
  return (
    <div
      className={`
        fixed top-0 h-dvh w-screen

        lg:hidden

        ${isOpen ? 'z-[10000000]' : 'hidden'}
      `}
    >
      <aside
        className={`
          fixed top-0 flex h-dvh w-[0] flex-col overflow-hidden bg-white
          shadow-nav transition-all duration-100

          sm:shadow-none

          ${isOpenStyles()}
        `}
      >
        <HeaderTopBanner />

        <div className="flex min-h-0 flex-1 flex-col justify-between">
          <header>
            <div
              className={`flex items-center justify-between bg-white px-4 py-4`}
            >
              {/* LEFT */}
              <Link to="/" onClick={onClose}>
                <img
                  src="/iso-go-light.png"
                  alt="Gama Optima isotipo"
                  className="h-9 w-9 object-contain"
                />
              </Link>

              <button type="button" onClick={onClose}>
                <Icon name="basic/x" size="20px" color={'#01A8B2'} />
              </button>
            </div>
            <Separator
              className={`!rounded-lg bg-line-gradient-dark opacity-[37%]`}
            />
          </header>

          <div className="flex flex-col">
            {getDrawerLinks().map((link) => (
              <DrawerLink
                key={link.href}
                href={link.href}
                isActive={isActive({ path: link.href })}
                title={link.title}
                close={onClose}
              />
            ))}
          </div>

          <div className="mt-4 px-4 pt-4">
            <Link to="/contacto" onClick={onClose}>
              <Button
                variant={'outlineInput'}
                className={`
                  flex h-12 w-full items-center justify-center gap-2 rounded-lg
                  border-none bg-primary-500 px-2 !transition-none

                  hover:bg-primary-600
                `}
              >
                <Icon size="16px" className="text-white" name="basic/mail" />
                <p className={`text-nowrap text-base-bold text-white`}>
                  Contáctanos
                </p>
              </Button>
            </Link>
            <a
              href="tel:919015000"
              className={`
                mt-8 flex items-center justify-center gap-2 text-neutral-900
              `}
            >
              <Icon name="basic/phone-call" size="20px" color={'#01A8B2'} />
              <p className={`text-base-bold`}>919 015 000</p>
            </a>
          </div>

          <div
            className={`
              z-[10000] mb-8 mt-auto flex items-center justify-center gap-3 px-4
              py-4
            `}
          >
            <a
              href="https://www.instagram.com/gamaoptima"
              target="_blank"
              rel="noopener noreferrer"
              onClick={onClose}
              className={`
                flex h-12 w-12 items-center justify-center rounded-full
                bg-primary-800
              `}
            >
              <img
                className="h-16 w-16 object-contain"
                src="/img/instagram.png"
                alt="instagram"
              />
            </a>
            <a
              href="https://www.youtube.com/@GamaOptima"
              target="_blank"
              rel="noopener noreferrer"
              onClick={onClose}
              className={`
                flex h-12 w-12 items-center justify-center rounded-full
                bg-primary-800
              `}
            >
              <img
                className="h-8 w-8 object-contain"
                src="/img/youtube.png"
                alt="youtube"
              />
            </a>
            <a
              href="https://www.tiktok.com/@gamaoptima"
              target="_blank"
              rel="noopener noreferrer"
              onClick={onClose}
              className={`
                flex h-12 w-12 items-center justify-center rounded-full
                bg-primary-800
              `}
            >
              <img
                className="h-10 w-10 object-contain"
                src="/img/tiktok.png"
                alt="tiktok"
              />
            </a>
            <a
              href="https://www.facebook.com/profile.php?id=61583930866188"
              target="_blank"
              rel="noopener noreferrer"
              onClick={onClose}
              className={`
                flex h-12 w-12 items-center justify-center rounded-full
                bg-primary-800
              `}
            >
              <img
                className="h-14 w-14 object-contain"
                src="/img/fb.png"
                alt="facebook"
              />
            </a>
          </div>
        </div>
      </aside>
    </div>
  )
}

function DrawerLink({
  title,
  href,
  isActive,
  close,
}: {
  title: string
  href: string
  isActive: boolean
  close: () => void
}) {
  // Algunas entradas apuntan a apps externas (p. ej. "Encarga un coche"),
  // que no las puede resolver el router de Remix.
  const isExternal = href.startsWith('http')
  const className = `
    flex h-14 items-center justify-between border-b border-neutral-100 px-4
    text-[20px] font-bold text-primary-800

    hover:bg-neutral-25

    ${isActive ? '!text-primary-600' : ''}
  `
  const content = (
    <>
      <span>{title}</span>

      <svg
        width="8"
        height="13"
        viewBox="0 0 8 13"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <path
          d="M1.06055 1.06066L6.06055 6.06066L1.06055 11.0607"
          stroke="#004851"
          strokeWidth="1.5"
          strokeLinecap="square"
        />
      </svg>
    </>
  )

  if (isExternal) {
    return (
      <a
        className={className}
        href={href}
        onClick={() => {
          close()
        }}
        {...EXTERNAL_LINK_PROPS}
      >
        {content}
      </a>
    )
  }

  return (
    <Link
      className={className}
      to={`${href}`}
      onClick={() => {
        close()
      }}
      prefetch="intent"
    >
      {content}
    </Link>
  )
}

export default DrawerNavigator
